import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import type { IApiResponse, IApiPaginatedResponse } from "@/types";
import type {
  IOvertimeRequest,
  IOvertimeSummary,
  IOvertimeQuota,
  OvertimeRequestType,
  OvertimeRequestStatus,
} from "@/types/overtime";

// ─── Query param shapes ───────────────────────────────────────────────────────

export interface IMsGetMyOvertimeRequestsParams {
  page?: number;
  limit?: number;
  status?: OvertimeRequestStatus;
  type?: OvertimeRequestType;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  userId?: number;
  branchId?: number;
}

export interface IMsCreateOvertimeRequestData {
  date: string;
  requestedStart: string;
  requestedEnd: string;
  reason?: string;
  type?: OvertimeRequestType;
  attachment?: File | null;
}

export interface IMsUpdateOvertimeRequestData {
  id: number;
  date?: string;
  requestedStart?: string;
  requestedEnd?: string;
  reason?: string;
  attachment?: File | null;
}

// ─── API slice ────────────────────────────────────────────────────────────────

export const msOvertimeApi = createApi({
  reducerPath: "msOvertimeApi",
  refetchOnFocus: false,
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "msGetMyOvertimeRequests",
    "msGetOvertimeRequestById",
    "msGetMyOvertimeSummary",
    "msGetOvertimeQuota",
  ],
  endpoints: (builder) => ({
    msGetMyOvertimeRequests: builder.query<
      IApiPaginatedResponse<IOvertimeRequest[]>,
      IMsGetMyOvertimeRequestsParams
    >({
      query: (params) => ({
        url: "/employee/overtime-requests/paginated",
        method: "GET",
        params,
      }),
      providesTags: ["msGetMyOvertimeRequests"],
    }),

    msGetOvertimeRequestById: builder.query<
      IApiResponse<IOvertimeRequest>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/employee/overtime-requests/${id}`,
        method: "GET",
      }),
      providesTags: ["msGetOvertimeRequestById"],
    }),

    msGetMyOvertimeSummary: builder.query<
      IApiResponse<IOvertimeSummary>,
      void
    >({
      query: () => ({
        url: "/employee/overtime-requests/me/summary",
        method: "GET",
      }),
      providesTags: ["msGetMyOvertimeSummary"],
    }),

    msCreateOvertimeRequest: builder.mutation<
      IApiResponse<IOvertimeRequest>,
      IMsCreateOvertimeRequestData
    >({
      query: (body) => {
        const { attachment, ...rest } = body;
        const formData = new FormData();
        formData.append("date", rest.date);
        formData.append("requestedStart", rest.requestedStart);
        formData.append("requestedEnd", rest.requestedEnd);
        if (rest.reason) formData.append("reason", rest.reason);
        if (rest.type) formData.append("type", rest.type);
        if (attachment instanceof File) formData.append("attachment", attachment);
        return {
          url: "/employee/overtime-requests",
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: ["msGetMyOvertimeRequests", "msGetMyOvertimeSummary"],
    }),

    msUpdateOvertimeRequest: builder.mutation<
      IApiResponse<IOvertimeRequest>,
      IMsUpdateOvertimeRequestData
    >({
      query: ({ id, ...body }) => {
        const formData = new FormData();
        if (body.date) formData.append("date", body.date);
        if (body.requestedStart) formData.append("requestedStart", body.requestedStart);
        if (body.requestedEnd) formData.append("requestedEnd", body.requestedEnd);
        if (body.reason) formData.append("reason", body.reason);
        if (body.attachment instanceof File) formData.append("attachment", body.attachment);
        return {
          url: `/employee/overtime-requests/${id}`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: [
        "msGetMyOvertimeRequests",
        "msGetOvertimeRequestById",
        "msGetMyOvertimeSummary",
      ],
    }),

    msCancelOvertimeRequest: builder.mutation<
      IApiResponse<void>,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/employee/overtime-requests/${id}/cancel`,
        method: "POST",
      }),
      invalidatesTags: ["msGetMyOvertimeRequests", "msGetMyOvertimeSummary"],
    }),

    msGetOvertimeQuota: builder.query<
      IApiResponse<IOvertimeQuota | null>,
      { date: string }
    >({
      query: ({ date }) => ({
        url: "/employee/overtime-requests/quota",
        method: "GET",
        params: { date },
      }),
      providesTags: ["msGetOvertimeQuota"],
    }),
  }),
});

export const {
  useMsGetMyOvertimeRequestsQuery,
  useMsGetOvertimeRequestByIdQuery,
  useMsGetMyOvertimeSummaryQuery,
  useMsCreateOvertimeRequestMutation,
  useMsUpdateOvertimeRequestMutation,
  useMsCancelOvertimeRequestMutation,
  useMsGetOvertimeQuotaQuery,
} = msOvertimeApi;
